import { DarwinCol, DarwinRow, DarwinTooltip } from 'components/darwin-ui';
import { Input } from 'antd';
import { GenericInput } from 'components/ui/GenericInput';
import {
  PROCESS_BLOCK_DESCRIPTION_MAX_LENGTH,
  PROCESS_BLOCK_NAME_MAX_LENGTH,
} from 'constants/processFlow';
import { useCallback, useEffect, useState } from 'react';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputType } from 'types/genericTypes';
import {
  EditProcessBlockForm,
  ProcessesBlockInputProps,
} from 'types/processBlockTypes';
import { Form } from 'antd';
import styles from '../../Ui.module.css';

interface ConfigureFormSectionProps {
  control: Control<EditProcessBlockForm>;
  errors: FieldErrors<EditProcessBlockForm>;
  endOfFlow: boolean;
  initialFlow: boolean;
  isCheckedEndOfFlow: boolean;
  isCheckedInitialFlow: boolean;
}

export const ConfigureFormSection = ({
  control,
  errors,
  endOfFlow,
  initialFlow,
  isCheckedEndOfFlow,
  isCheckedInitialFlow,
}: ConfigureFormSectionProps) => {
  const { t } = useTranslation();

  const [isPressedEndOfFlow, setIsCheckedEndOfFlow] =
    useState(isCheckedEndOfFlow);
  const [isPressedInitialFlow, setIsCheckedInitialFlow] =
    useState(isCheckedInitialFlow);

  const [showTooltip, setShowTooltip] = useState(false);
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(
    null
  );
  const inputsValue = useWatch({ control, name: 'inputs' });

  useEffect(() => {
    if (inputElement) {
      const { scrollWidth, clientWidth } = inputElement;
      setShowTooltip(scrollWidth > clientWidth);
    }
  }, [inputsValue, inputElement]);

  useEffect(() => {
    // Reset the state when the component mounts or when the props change
    if (typeof isCheckedEndOfFlow === 'boolean') {
      setIsCheckedEndOfFlow(isCheckedEndOfFlow);
    }

    if (typeof isCheckedInitialFlow === 'boolean') {
      setIsCheckedInitialFlow(isCheckedInitialFlow);
    }
  }, [isCheckedInitialFlow, isCheckedEndOfFlow]);

  const onCheckEndOrInitialFlow = useCallback(
    (fieldName: string, value: boolean) => {
      if (fieldName === 'isEndOfFlow') {
        setIsCheckedEndOfFlow(value);
        if (value) setIsCheckedInitialFlow(false);
      } else {
        setIsCheckedInitialFlow(value);
        if (value) setIsCheckedEndOfFlow(false);
      }
    },
    []
  );

  const inputs: ProcessesBlockInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'name',
      label: t('process-flow.configure.name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('process-flow.configure.name-required'),
        maxLength: {
          value: PROCESS_BLOCK_NAME_MAX_LENGTH,
          message: t('process-flow.configure.max-length-error', {
            fieldName: t('process-flow.configure.name'),
            maxLength: PROCESS_BLOCK_NAME_MAX_LENGTH,
          }),
        },
      },
      maxLength: PROCESS_BLOCK_NAME_MAX_LENGTH,
    },
    {
      inputType: InputType.TEXTAREA,
      fieldName: 'description',
      label: t('process-flow.configure.description'),
      showCount: true,
      inputRules: {
        maxLength: {
          value: PROCESS_BLOCK_DESCRIPTION_MAX_LENGTH,
          message: t('process-flow.configure.max-length-error', {
            fieldName: t('process-flow.configure.description'),
            maxLength: PROCESS_BLOCK_DESCRIPTION_MAX_LENGTH,
          }),
        },
      },
      maxLength: PROCESS_BLOCK_DESCRIPTION_MAX_LENGTH,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'processType',
      label: t('process-flow.configure.process-type'),
      disabled: true,
      inputRules: {},
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'inputs',
      label: t('process-flow.configure.input'),
      disabled: true,
      inputRules: {},
    },
  ];

  if (endOfFlow) {
    inputs.push({
      inputType: InputType.CHECKBOX,
      isCheckboxChecked: isCheckedEndOfFlow,
      disabled: isPressedInitialFlow,
      checkboxText: t('process-flow.configure.checkbox-text-end-flow'),
      fieldName: 'isEndOfFlow',
      label: t('process-flow.configure.is-end-flow'),
      showRequiredAsterisk: false,
      inputRules: {},
      onCheckEndOrInitialFlow: onCheckEndOrInitialFlow,
    });
  }

  if (initialFlow) {
    inputs.push({
      inputType: InputType.CHECKBOX,
      isCheckboxChecked: isCheckedInitialFlow,
      disabled: isPressedEndOfFlow,
      checkboxText: t('process-flow.configure.checkbox-text-initial-flow'),
      fieldName: 'isInitOfFlow',
      label: t('process-flow.configure.is-initial-flow'),
      showRequiredAsterisk: false,
      inputRules: {},
      onCheckEndOrInitialFlow: onCheckEndOrInitialFlow,
    });
  }

  return (
    <>
      <DarwinRow gutter={16}>
        <DarwinCol span={12}>
          {inputs.map(input => {
            if (input.fieldName === 'inputs') {
              return (
                <Form.Item
                  label={input.label}
                  validateStatus={errors[input.fieldName] ? 'error' : ''}
                  help={
                    errors[input.fieldName]?.message
                      ? String(errors[input.fieldName]?.message)
                      : undefined
                  }
                  className={styles.formInputTitle}
                >
                  <Controller
                    name={input.fieldName}
                    control={control}
                    disabled={input.disabled}
                    render={({ field }) => (
                      <DarwinTooltip
                        title={field.value}
                        open={showTooltip ? undefined : false}
                        placement="right"
                      >
                        <Input
                          {...field}
                          ref={el => setInputElement(el?.input || null)}
                        />
                      </DarwinTooltip>
                    )}
                  />
                </Form.Item>
              );
            }
            return (
              <GenericInput
                key={input.fieldName}
                control={control}
                errors={errors}
                {...input}
              />
            );
          })}
        </DarwinCol>
      </DarwinRow>
    </>
  );
};
